import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { OperationItemPicker } from '@/modules/commercial/components';
import { useClients } from '@/modules/commercial/hooks';
import {
  getCommercialOperation,
  saveCommercialOperation,
} from '@/modules/commercial/services';
import {
  COMMERCIAL_OPERATION_STATUS_LABELS,
  COMMERCIAL_OPERATION_TYPE_LABELS,
  type CommercialOperationInput,
  type CommercialOperationStatus,
  type CommercialOperationType,
} from '@/modules/commercial/types';
import { parseCommercialOperationPrefill } from '@/modules/commercial/utils/commercialUrl';
import { validateCommercialOperationInput } from '@/modules/commercial/utils/validation';
import { listLogisticsOrdersForReservation } from '@/modules/logistics/services';
import type { LogisticsOrder } from '@/modules/logistics/types';
import { listItems } from '@/modules/inventory/services';
import { listReservations } from '@/modules/reservations/services';
import type { InventoryItem } from '@/modules/inventory/types';
import type { Reservation } from '@/modules/reservations/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const defaultInput: CommercialOperationInput = {
  clientId: null,
  type: 'quotation',
  status: 'draft',
  title: '',
  description: '',
  reservationId: null,
  logisticsOrderIds: [],
  inventoryItemIds: [],
  startDate: '',
  endDate: '',
  subtotal: 0,
  taxAmount: 0,
  total: 0,
  notes: '',
};

const WRITABLE_STATUSES: CommercialOperationStatus[] = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'in_progress',
  'completed',
  'cancelled',
];

export function OperationFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('commercial.write');
  const { clients } = useClients();

  const [form, setForm] = useState<CommercialOperationInput>(defaultInput);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reservationOrders, setReservationOrders] = useState<LogisticsOrder[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([listItems(), listReservations()]).then(([allItems, res]) => {
      setItems(allItems);
      setReservations(res);
    });
  }, []);

  useEffect(() => {
    if (!isNew) return;
    const prefill = parseCommercialOperationPrefill(searchParams);
    setForm((prev) => ({
      ...prev,
      ...(prefill.clientId ? { clientId: prefill.clientId } : {}),
      ...(prefill.reservationId ? { reservationId: prefill.reservationId } : {}),
      ...(prefill.type ? { type: prefill.type } : {}),
    }));
  }, [isNew, searchParams]);

  useEffect(() => {
    if (!form.reservationId) {
      setReservationOrders([]);
      return;
    }
    const res = reservations.find((r) => r.id === form.reservationId);
    if (res && isNew) {
      setForm((prev) => ({
        ...prev,
        startDate: prev.startDate || res.startDate,
        endDate: prev.endDate || res.endDate,
        inventoryItemIds:
          prev.inventoryItemIds.length > 0
            ? prev.inventoryItemIds
            : [...res.inventoryItemIds],
        title: prev.title || res.title,
      }));
    }
    void listLogisticsOrdersForReservation(form.reservationId).then(
      setReservationOrders,
    );
  }, [form.reservationId, reservations, isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getCommercialOperation(id)
      .then((op) => {
        if (!op) {
          setError('Operación no encontrada.');
          return;
        }
        setForm({
          clientId: op.clientId,
          type: op.type,
          status: op.status,
          title: op.title,
          description: op.description,
          reservationId: op.reservationId,
          logisticsOrderIds: op.logisticsOrderIds,
          inventoryItemIds: op.inventoryItemIds,
          startDate: op.startDate,
          endDate: op.endDate,
          subtotal: op.subtotal,
          taxAmount: op.taxAmount,
          total: op.total,
          notes: op.notes,
        });
      })
      .catch(() => setError('Error al cargar la operación.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function toggleLogisticsOrder(orderId: string) {
    if (!canWrite) return;
    setForm((prev) => {
      const has = prev.logisticsOrderIds.includes(orderId);
      return {
        ...prev,
        logisticsOrderIds: has
          ? prev.logisticsOrderIds.filter((x) => x !== orderId)
          : [...prev.logisticsOrderIds, orderId],
      };
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !profile) return;
    const validation = await validateCommercialOperationInput(form);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveCommercialOperation(
        form,
        profile.uid,
        isNew ? undefined : id,
      );
      void navigate(`${modulePaths.commercial}/operaciones/${saved.id}`, {
        replace: true,
      });
    } catch {
      setError('No se pudo guardar la operación.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  const showRentalDates = form.type === 'rental';
  const showReservation = form.type === 'rental' || form.type === 'quotation';

  return (
    <section>
      <p>
        <Link to={`${modulePaths.commercial}/operaciones`}>
          ← Operaciones
        </Link>
      </p>
      <h1>{isNew ? 'Nueva operación' : 'Editar operación'}</h1>

      {!canWrite ? (
        <p className="auth-error">No tenés permiso para editar operaciones.</p>
      ) : (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <div className="users-form-grid">
            <label className="auth-label">
              Tipo
              <select
                value={form.type}
                onChange={(e) => {
                  const type = e.target.value as CommercialOperationType;
                  setForm({
                    ...form,
                    type,
                    ...(type === 'sale' ? { reservationId: null } : {}),
                  });
                }}
              >
                {(Object.keys(COMMERCIAL_OPERATION_TYPE_LABELS) as CommercialOperationType[]).map(
                  (t) => (
                    <option key={t} value={t}>
                      {COMMERCIAL_OPERATION_TYPE_LABELS[t]}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="auth-label">
              Estado
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value as CommercialOperationStatus,
                  })
                }
              >
                {WRITABLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {COMMERCIAL_OPERATION_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-label">
              Cliente
              <select
                value={form.clientId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    clientId: e.target.value || null,
                  })
                }
              >
                <option value="">— Sin cliente —</option>
                {clients
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="auth-label">
              Título *
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
          </div>

          <label className="auth-label">
            Descripción
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>

          {showRentalDates ? (
            <div className="users-form-grid">
              <label className="auth-label">
                Inicio
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
              </label>
              <label className="auth-label">
                Fin
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </label>
            </div>
          ) : null}

          {showReservation ? (
            <label className="auth-label">
              Reserva asociada
              <select
                value={form.reservationId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    reservationId: e.target.value || null,
                    logisticsOrderIds: [],
                  })
                }
              >
                <option value="">— Sin reserva —</option>
                {reservations.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title} ({r.startDate} → {r.endDate})
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {form.reservationId && reservationOrders.length > 0 ? (
            <fieldset className="inventory-panel">
              <legend>Órdenes logísticas vinculadas</legend>
              <ul className="reservation-item-picker">
                {reservationOrders.map((o) => (
                  <li key={o.id}>
                    <label className="auth-label auth-label--row">
                      <input
                        type="checkbox"
                        checked={form.logisticsOrderIds.includes(o.id)}
                        onChange={() => toggleLogisticsOrder(o.id)}
                      />
                      <span>
                        {o.type} — {o.scheduledDate} ({o.status})
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          ) : null}

          <h2>Artículos</h2>
          <OperationItemPicker
            items={items}
            operationType={form.type}
            selectedIds={form.inventoryItemIds}
            onChange={(ids) => setForm({ ...form, inventoryItemIds: ids })}
          />

          <h2>Importes (preparación facturación)</h2>
          <div className="users-form-grid">
            <label className="auth-label">
              Subtotal
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.subtotal || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    subtotal: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
            <label className="auth-label">
              Impuestos (placeholder)
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.taxAmount || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    taxAmount: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
            <label className="auth-label">
              Total
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.total || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    total: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
          </div>

          <label className="auth-label">
            Notas
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          {error ? <p className="auth-error">{error}</p> : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <Link
              to={`${modulePaths.commercial}/operaciones`}
              className="auth-button auth-button--secondary"
            >
              Cancelar
            </Link>
          </div>
        </form>
      )}
    </section>
  );
}
